import { useState } from 'react';
import { motion } from 'framer-motion';
import type { HabitWithStreak, HabitCompletion } from '@/types';
import { StreakBadge } from '@/components/gamification';
import { CATEGORY_COLORS, CATEGORY_ICONS, IconByName } from '@/lib/category-icons';

interface HabitCardProps {
  habit: HabitWithStreak;
  onComplete: (habitId: string, notes?: string, moodRating?: number) => Promise<HabitCompletion>;
  onEdit?: (habit: HabitWithStreak) => void;
  onDelete?: (habitId: string) => void;
}

export function HabitCard({ habit, onComplete, onEdit, onDelete }: HabitCardProps) {
  const [isCompleting, setIsCompleting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showQuickNote, setShowQuickNote] = useState(false);
  const [quickNote, setQuickNote] = useState('');

  const defaultColors = { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' };
  const colors = CATEGORY_COLORS[habit.category] ?? defaultColors;

  async function handleComplete() {
    if (habit.completedToday || isCompleting) return;

    setIsCompleting(true);
    try {
      await onComplete(habit.id, quickNote || undefined);
      setQuickNote('');
      setShowQuickNote(false);
    } finally {
      setIsCompleting(false);
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`relative p-4 rounded-xl border ${colors.border} ${colors.bg}
                 transition-all hover:shadow-lg group`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <IconByName name={habit.icon} className="w-6 h-6 text-slate-300" />
          <div>
            <h3 className="font-semibold text-white">{habit.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs capitalize ${colors.text} flex items-center gap-1`}>
                {(() => { const Icon = CATEGORY_ICONS[habit.category]; return Icon ? <Icon className="w-3 h-3" /> : null; })()}
                {habit.category}
              </span>
              <span className="text-xs text-slate-500">
                {habit.frequency === 'daily' ? 'Daily' :
                 habit.frequency === 'weekly' ? 'Weekly' :
                 habit.frequency === 'biweekly' ? 'Bi-weekly' : 'Monthly'}
              </span>
            </div>
          </div>
        </div>

        {/* Menu button */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-slate-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-36 bg-charcoal-800 rounded-lg shadow-xl border border-charcoal-700 py-1">
                {onEdit && (
                  <button
                    onClick={() => { onEdit(habit); setShowMenu(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-charcoal-700"
                  >
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => { onDelete(habit.id); setShowMenu(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-charcoal-700"
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Description */}
      {habit.description && (
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{habit.description}</p>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-3 mb-4">
        <StreakBadge streak={habit.currentStreak} size="sm" />
        <span className="text-xs text-slate-500">
          +{habit.xpPerCompletion} XP per check-in
        </span>
      </div>

      {/* Quick note input */}
      {showQuickNote && !habit.completedToday && (
        <div className="mb-3">
          <input
            type="text"
            value={quickNote}
            onChange={(e) => setQuickNote(e.target.value)}
            placeholder="Add a note (optional)..."
            className="w-full px-3 py-2 text-sm rounded-lg bg-charcoal-800 border border-charcoal-700
                     text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {habit.completedToday ? (
          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/20 text-emerald-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">Completed</span>
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowQuickNote(!showQuickNote)}
              className="px-3 py-2.5 rounded-lg bg-charcoal-800 text-slate-400 hover:text-white
                       border border-charcoal-700 hover:border-charcoal-600 transition-colors"
              title="Add note"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={handleComplete}
              disabled={isCompleting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                       bg-royal-gradient text-white font-medium shadow-glow
                       hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {isCompleting ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Checking in...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Check In</span>
                </>
              )}
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}
